import { useEffect, useRef, useState } from 'react';
import type { KYCServiceState, KYCServiceType } from 'consts/kyc';
import { PER_PAGE_SIZE } from 'consts/transactions';
import { getMerchantKYCSettings } from 'features/merchants/api';
import { selectActiveMerchants } from 'features/merchants/selectors';
import { getMerchantsMethodsPivot } from 'features/pivots/api';
import type { Merchant, MerchantKYCService } from 'features/merchants/types';
import type { MerchantsMethodsPivot } from 'features/pivots/types';
import useFetchMerchantList from 'ui/organizms/Merchants/EnabledMerchants/useFetchMerchantList';
import useFetchResource from 'hooks/useFetchResource';
import { useTypedSelector } from 'hooks';
import type { ID, PaginateResourceResponse } from 'types';
import { compareIds } from 'util/support';
import {
    isServiceSoft,
    isServiceStrict
} from '../Services/helpers';
import { KYCServiceState as KYCServiceStateEnum } from 'consts/kyc';
import type { MerchantKycSummary } from './types';

const computeServiceState = (
    service: Pick<MerchantKYCService, 'kycLimit' | 'summedAmountEurLimit'>
): KYCServiceState => {
    if (isServiceSoft(service)) {
        return KYCServiceStateEnum.Soft;
    }
    if (isServiceStrict(service)) {
        return KYCServiceStateEnum.Strict;
    }
    return KYCServiceStateEnum.Off;
};

const groupBy = <T,>(items: ReadonlyArray<T>, key: (item: T) => ID) => {
    const result = new Map<string, Array<T>>();
    items.forEach(item => {
        const id = String(key(item));
        const bucket = result.get(id);
        if (bucket) {
            bucket.push(item);
        } else {
            result.set(id, [item]);
        }
    });
    return result;
};

export default function useMerchantKycSummaries() {
    const { isLoading: isMerchantsLoading } = useFetchMerchantList();
    const merchants = useTypedSelector(selectActiveMerchants) as Array<Merchant>;

    const [services, setServices] = useState<Array<MerchantKYCService>>([]);
    const [methods, setMethods] = useState<Array<MerchantsMethodsPivot>>([]);
    const [isAuxLoading, setIsAuxLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    const isFetchingRef = useRef(false);

    const fetchServicesRef = useRef(
        useFetchResource({ shouldFetchAll: true, perPage: PER_PAGE_SIZE })
    );
    const fetchMethodsRef = useRef(
        useFetchResource({ shouldFetchAll: true, perPage: PER_PAGE_SIZE })
    );

    useEffect(() => {
        if (isFetchingRef.current) {
            return;
        }
        isFetchingRef.current = true;
        setIsAuxLoading(true);

        Promise.allSettled([
            fetchServicesRef.current<MerchantKYCService>(queryString =>
                getMerchantKYCSettings(queryString)),
            fetchMethodsRef.current<MerchantsMethodsPivot>(queryString =>
                getMerchantsMethodsPivot(queryString))
        ])
            .then(([servicesResult, methodsResult]) => {
                if (servicesResult.status === 'fulfilled') {
                    const response =
                        servicesResult.value as PaginateResourceResponse<MerchantKYCService>;
                    setServices(response.data?.data ?? []);
                }
                if (methodsResult.status === 'fulfilled') {
                    const response =
                        methodsResult.value as PaginateResourceResponse<MerchantsMethodsPivot>;
                    setMethods(response.data?.data ?? []);
                }
            })
            .finally(() => {
                isFetchingRef.current = false;
                setIsAuxLoading(false);
            });
    }, [reloadToken]);

    const servicesByMerchant = groupBy(services, s => s.merchantId);
    const methodsByMerchant = groupBy(methods, m => m.merchantId);

    const summaries: Array<MerchantKycSummary> = merchants.map(merchant => {
        const merchantServices = servicesByMerchant.get(String(merchant.coreId)) ?? [];
        const merchantMethods = methodsByMerchant.get(String(merchant.coreId)) ?? [];

        const serviceStates: Partial<Record<KYCServiceType, KYCServiceState>> = {};
        merchantServices.forEach(svc => {
            serviceStates[svc.kycTypeId] = computeServiceState(svc);
        });

        // Use the most common non-empty provider across services
        const providerCounts = new Map<string, number>();
        merchantServices.forEach(svc => {
            if (svc.provider) {
                providerCounts.set(svc.provider, (providerCounts.get(svc.provider) ?? 0) + 1);
            }
        });
        let kycProvider = '';
        let maxCount = 0;
        providerCounts.forEach((count, provider) => {
            if (count > maxCount) {
                maxCount = count;
                kycProvider = provider;
            }
        });

        const includedMethods = merchantMethods
            .filter(m => Boolean(Number(m.shouldKyc)))
            .map(m => ({ id: String(m.coreId), name: m.paymentMethodName }))
            .filter(({ name }) => Boolean(name));

        return {
            merchant,
            kycProvider,
            serviceStates,
            includedMethods
        };
    });

    const reload = () => setReloadToken(token => token + 1);

    return {
        summaries,
        isLoading: isMerchantsLoading || isAuxLoading,
        reload,
        getSummary: (merchantId: ID) =>
            summaries.find(({ merchant }) => compareIds(merchant.coreId, merchantId))
    };
}
